// DEPRECATED: consolidated into invariantAggregators.js — kept for backward compat
// ─────────────────────────────────────────────────────────────────────────────
// HeadToHeadAggregator.js
// All pro-PC vs. anti-PC head-to-head statement pairs, grouped by theme.
// Pro = teal, Anti = coral/red-orange (matches deck diverging bar style).
// ─────────────────────────────────────────────────────────────────────────────

// Shared pro/anti palette — consistent across every HH question
const PRO  = "#1A7A78"; // teal  — pro-industry / pro-PC side
const ANTI = "#C0392B"; // red   — anti-industry / regulatory side

export function getHHColours() {
  return [PRO, ANTI];
}

export function getHHConfig() {
  const groupsById = {

    // ── CHOICE ───────────────────────────────────────────────────────────────

    choice_hh1: {
      type: "likert",
      label: "CHOICE: Government role vs. personal autonomy",
      fulltext: "Which statement do you agree with more?",
      keys: [
        "choice_hh1__pro_pc_pred_n",
        "choice_hh1__anti_pc_pred_n",
      ],
      labels: {
        "choice_hh1__pro_pc_pred_n":  "People should be free to decide which personal care products to use for themselves and their families, as long as products meet science-based safety standards.",
        "choice_hh1__anti_pc_pred_n": "The government should restrict certain personal care products to protect people from potential long-term health risks.",
      },
      colors: {
        "choice_hh1__pro_pc_pred_n":  PRO,
        "choice_hh1__anti_pc_pred_n": ANTI,
      },
      polarity: {
        pos: "choice_hh1__pro_pc_pred_n",
        neg: "choice_hh1__anti_pc_pred_n",
      },
      schema: "single",
    },

    choice_hh2: {
      type: "likert",
      label: "CHOICE: Parents vs. precautionary regulation",
      fulltext: "Which statement do you agree with more?",
      keys: [
        "choice_hh2__pro_pc_pred_n",
        "choice_hh2__anti_pc_pred_n",
      ],
      labels: {
        "choice_hh2__pro_pc_pred_n":  "Parents are best positioned to decide what personal care products are appropriate for their children, based on individual needs and guidance from professionals.",
        "choice_hh2__anti_pc_pred_n": "Children need extra protection, and the government should limit access to certain personal care products for minors, even if parents disagree.",
      },
      colors: {
        "choice_hh2__pro_pc_pred_n":  PRO,
        "choice_hh2__anti_pc_pred_n": ANTI,
      },
      polarity: {
        pos: "choice_hh2__pro_pc_pred_n",
        neg: "choice_hh2__anti_pc_pred_n",
      },
      schema: "single",
    },

    choice_hh3: {
      type: "likert",
      label: "CHOICE: Broad bans are not helpful vs. Broad bans are needed",
      fulltext: "Which statement do you agree with more?",
      keys: [
        "choice_hh3__pro_pc_pred_n",
        "choice_hh3__anti_pc_pred_n",
      ],
      labels: {
        "choice_hh3__pro_pc_pred_n":  "Policies intended to prevent harm should be based on clear scientific evidence and real-world exposure. Overly broad bans can remove safe products from the market without improving health outcomes.",
        "choice_hh3__anti_pc_pred_n": "Current regulations do not go far enough to protect consumers, and stronger state-level policies are needed to close gaps and reduce exposure to potentially harmful ingredients.",
      },
      colors: {
        "choice_hh3__pro_pc_pred_n":  PRO,
        "choice_hh3__anti_pc_pred_n": ANTI,
      },
      polarity: {
        pos: "choice_hh3__pro_pc_pred_n",
        neg: "choice_hh3__anti_pc_pred_n",
      },
      schema: "single",
    },

    // ── ECONOMY ──────────────────────────────────────────────────────────────

    econ_hh1: {
      type: "likert",
      label: "ECONOMIC IMPACT: Jobs and access vs. restrictions",
      fulltext: "Which statement do you agree with more?",
      keys: [
        "econ_hh1__pro_pc_pred_n",
        "econ_hh1__anti_pc_pred_n",
      ],
      labels: {
        "econ_hh1__pro_pc_pred_n":  "Personal care products support millions of jobs and small businesses, and policies should protect both safety and continued access, so communities and the people who work in the industry aren’t harmed.",
        "econ_hh1__anti_pc_pred_n": "If restricting or banning certain products protects health or the environment, job losses or business impacts may be an acceptable tradeoff.",
      },
      colors: {
        "econ_hh1__pro_pc_pred_n":  PRO,
        "econ_hh1__anti_pc_pred_n": ANTI,
      },
      polarity: {
        pos: "econ_hh1__pro_pc_pred_n",
        neg: "econ_hh1__anti_pc_pred_n",
      },
      schema: "single",
    },

    econ_hh2: {
      type: "likert",
      label: "ECONOMIC IMPACT: Availability vs. price",
      fulltext: "Which statement do you agree with more?",
      keys: [
        "econ_hh2__pro_pc_pred_n",
        "econ_hh2__anti_pc_pred_n",
      ],
      labels: {
        "econ_hh2__pro_pc_pred_n":  "Policies should account for how restrictions may reduce product availability, narrow options, and making it harder for shoppers to find everyday essentials that fit their budgets.",
        "econ_hh2__anti_pc_pred_n": "Protecting health and the environment is more important than keeping products inexpensive or widely available.",
      },
      colors: {
        "econ_hh2__pro_pc_pred_n":  PRO,
        "econ_hh2__anti_pc_pred_n": ANTI,
      },
      polarity: {
        pos: "econ_hh2__pro_pc_pred_n",
        neg: "econ_hh2__anti_pc_pred_n",
      },
      schema: "single",
    },

    // ── EVERYDAY ─────────────────────────────────────────────────────────────

    everyday_hh1: {
      type: "likert",
      label: "EVERYDAY SIGNIFICANCE: Essential vs. nonessential",
      fulltext: "Which statement do you agree with more?",
      keys: [
        "everyday_hh1__pro_pc_pred_n",
        "everyday_hh1__anti_pc_pred_n",
      ],
      labels: {
        "everyday_hh1__pro_pc_pred_n":  "Products like toothpaste, shampoo, deodorant, and sunscreen are essential parts of daily routines and public health, not optional luxuries.",
        "everyday_hh1__anti_pc_pred_n": "Personal care products are largely discretionary, and limiting or reformulating them would not significantly affect people’s daily lives.",
      },
      colors: {
        "everyday_hh1__pro_pc_pred_n":  PRO,
        "everyday_hh1__anti_pc_pred_n": ANTI,
      },
      polarity: {
        pos: "everyday_hh1__pro_pc_pred_n",
        neg: "everyday_hh1__anti_pc_pred_n",
      },
      schema: "single",
    },

    // ── SAFETY ───────────────────────────────────────────────────────────────

    safety_hh1: {
      type: "likert",
      label: "PRODUCT SAFETY: Science-based oversight vs. precautionary bans",
      fulltext: "Which statement do you agree with more?",
      keys: [
        "safety_hh1__pro_pc_pred_n",
        "safety_hh1__anti_pc_pred_n",
      ],
      labels: {
        "safety_hh1__pro_pc_pred_n":  "Not every detectable ingredient poses a real-world risk. Modern testing can identify trace amounts at extremely low levels, and safety decisions should be based on exposure and dosage, not just detection.",
        "safety_hh1__anti_pc_pred_n": "If an ingredient is linked to any potential hazard, it should be restricted or banned.",
      },
      colors: {
        "safety_hh1__pro_pc_pred_n":  PRO,
        "safety_hh1__anti_pc_pred_n": ANTI,
      },
      polarity: {
        pos: "safety_hh1__pro_pc_pred_n",
        neg: "safety_hh1__anti_pc_pred_n",
      },
      schema: "single",
    },

    safety_hh2: {
      type: "likert",
      label: "PRODUCT SAFETY: Regulated products vs. unintended consequences",
      fulltext: "Which statement do you agree with more?",
      keys: [
        "safety_hh2__pro_pc_pred_n",
        "safety_hh2__anti_pc_pred_n",
      ],
      labels: {
        "safety_hh2__pro_pc_pred_n":  "Overly broad restrictions can push consumers toward unregulated or counterfeit alternatives, which may be less safe than regulated products.",
        "safety_hh2__anti_pc_pred_n": "Removing products from the market is worth the risk if it reduces exposure to ingredients some experts consider concerning.",
      },
      colors: {
        "safety_hh2__pro_pc_pred_n":  PRO,
        "safety_hh2__anti_pc_pred_n": ANTI,
      },
      polarity: {
        pos: "safety_hh2__pro_pc_pred_n",
        neg: "safety_hh2__anti_pc_pred_n",
      },
      schema: "single",
    },

    safety_hh3: {
      type: "likert",
      label: "PRODUCT SAFETY: Safety for all vs. disproportional effects",
      fulltext: "Which statement do you agree with more?",
      keys: [
        "safety_hh3__pro_pc_pred_n",
        "safety_hh3__anti_pc_pred_n",
      ],
      labels: {
        "safety_hh3__pro_pc_pred_n":  "Personal care product safety standards apply to all consumers and are based on how products are actually used in real life. Safety evaluations consider exposure levels across different populations, and policy decisions should be grounded in scientific risk assessment.",
        "safety_hh3__anti_pc_pred_n": "Women of color and salon workers are disproportionately exposed to harmful ingredients in personal care products, and stronger regulations are needed to address these health disparities.",
      },
      colors: {
        "safety_hh3__pro_pc_pred_n":  PRO,
        "safety_hh3__anti_pc_pred_n": ANTI,
      },
      polarity: {
        pos: "safety_hh3__pro_pc_pred_n",
        neg: "safety_hh3__anti_pc_pred_n",
      },
      schema: "single",
    },

    // ── ENVIRONMENT ──────────────────────────────────────────────────────────

    envir_hh1: {
      type: "likert",
      label: "ENVIRONMENT: Innovation-led progress vs. immediate mandates",
      fulltext: "Which statement do you agree with more?",
      keys: [
        "envir_hh1__pro_pc_pred_n",
        "envir_hh1__anti_pc_pred_n",
      ],
      labels: {
        "envir_hh1__pro_pc_pred_n":  "The best way to improve sustainability in personal care products is through innovation and continuous improvement, guided by science and realistic timelines.",
        "envir_hh1__anti_pc_pred_n": "Voluntary efforts are not enough, and enforceable standards are needed to ensure all companies reduce their environmental impact.",
      },
      colors: {
        "envir_hh1__pro_pc_pred_n":  PRO,
        "envir_hh1__anti_pc_pred_n": ANTI,
      },
      polarity: {
        pos: "envir_hh1__pro_pc_pred_n",
        neg: "envir_hh1__anti_pc_pred_n",
      },
      schema: "single",
    },

  };

  return { groupsById };
}

export function buildHHLabelColorMaps(groupsById) {
  const labels = {};
  const colors = {};

  for (const g of Object.values(groupsById)) {
    for (const k of g.keys) {
      labels[k] = g.labels?.[k] || k;
      if (!colors[k]) {
        colors[k] = g.colors?.[k] ?? baseColorForLikertKey(g, k);
      }
    }
  }

  return { labels, colors };
}

export function baseColorForLikertKey(group, key) {
  const pol = group?.polarity || {};
  const tag = Object.entries(pol).find(([_, polKey]) => {
    if (Array.isArray(polKey)) return polKey.includes(key);
    return polKey === key;
  })?.[0];
  if (tag === "pos") return PRO;
  if (tag === "neg") return ANTI;
  if (tag === "neu") return "#8C9BA5";
  return "#607D8B";
}

// Tab-as-config default export
export default {
  tab: "invariant_h2h",
  label: "Invariant H2H",
  get metrics() { return getHHConfig().groupsById; },
};
